import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  dateTimeFormats,
  formatTypes,
} from "@/src/constants/dateTimeConstants.js";
import { calculateDateRange } from "@/src/constants/filterConstants.js";
import store from "@/src/store/index.js";

dayjs.extend(utc);

const isDateString = (v) =>
  typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v);

const toDate = (v) => v.substring(0, 10);

const dateOperatorRange = {
  before: (d) => ({
    start_date: "2000-01-01",
    end_date: dayjs(d).subtract(1, "day").format("YYYY-MM-DD"),
  }),
  after: (d) => ({
    start_date: dayjs(d).add(1, "day").format("YYYY-MM-DD"),
    end_date: dayjs().add(5, "year").format("YYYY-MM-DD"),
  }),
  is: (d) => ({ start_date: d, end_date: d }),
  equals: (d) => ({ start_date: d, end_date: d }),
};

export const toDateFilterFormat = (filterValue) => {
  if (!filterValue || typeof filterValue !== "object") return filterValue;
  if (filterValue.option && filterValue.start_date && filterValue.end_date)
    return filterValue;

  const { operator, value } = filterValue;
  if (!operator) return filterValue;

  if (isDateString(value)) {
    const rangeFn = dateOperatorRange[operator] || dateOperatorRange.is;
    return { option: "custom", ...rangeFn(toDate(value)) };
  }

  if (Array.isArray(value) && value.length >= 2) {
    return {
      option: operator === "not_between" ? "not_between" : "custom",
      start_date: toDate(String(value[0])),
      end_date: toDate(String(value[1])),
    };
  }

  if (typeof value === "string" && value !== "custom") {
    const range = calculateDateRange(value);
    if (range)
      return { option: value, start_date: range.start, end_date: range.end };
  }

  return filterValue;
};

export const resolveDateFilters = (filters, dateFieldKeys) => {
  if (!filters || Object.keys(filters).length === 0) return filters;

  const resolved = {};
  for (const [key, value] of Object.entries(filters)) {
    if (!value) {
      resolved[key] = value;
      continue;
    }

    if (value.option && value.start_date && value.end_date) {
      resolved[key] = value;
      continue;
    }

    if (dateFieldKeys?.has(key) && value.operator) {
      const dateFilter = toDateFilterFormat(value);
      if (dateFilter?.start_date && dateFilter?.end_date) {
        resolved[key] = dateFilter;
        continue;
      }
    }

    resolved[key] = value;
  }
  return resolved;
};

export const getMonthName = (month, year) => {
  const date = new Date(year, month - 1, 1);
  return date.toLocaleString("en-US", { month: "short" });
};

export const getInitialDateTime = () => {
  const now = dayjs();
  const minute = now.minute();
  const roundedMinute = Math.ceil(minute / 15) * 15;
  const nextTime = now.minute(roundedMinute).second(0);

  let hours = nextTime.hour();
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12;
  hours = hours === 0 ? 12 : hours;
  const minutes = nextTime.minute();
  const formattedTime = `${hours}:${minutes === 0 ? "00" : minutes}${ampm}`;

  return {
    due_date: nextTime,
    due_time: formattedTime,
  };
};

export const isISODateTimeString = (value) => {
  try {
    if (typeof value !== "string") return false;
    const isoPattern =
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;
    return isoPattern.test(value);
  } catch (error) {
    console.error(
      "An error occurred while ensuring is ISO date time string: ",
      error,
    );
    return false;
  }
};

export const formatISODate = (isoDate, format = dateTimeFormats[1]) => {
  try {
    if (!isoDate || !dayjs(isoDate).isValid()) {
      console.error("formatISODate: Invalid or missing date", { isoDate });
      return "";
    }
    return dayjs(isoDate).format(format);
  } catch (error) {
    console.error("formatISODate: Error", { error, isoDate });
    return "";
  }
};

export const calculateDateDiff = (targetDate, referenceDate = new Date()) => {
  try {
    if (!targetDate || !dayjs(targetDate).isValid()) {
      console.error("calculateDateDiff: Invalid or missing target date");
      return 0;
    }

    const target = dayjs(targetDate).startOf("day");
    const reference = dayjs(referenceDate).startOf("day");

    return Math.ceil(target.diff(reference, "day", true));
  } catch (error) {
    console.error("calculateDateDiff: Error", {
      error,
      targetDate,
      referenceDate,
    });
    return 0;
  }
};

export const isToday = (date) => {
  try {
    if (!date || !dayjs(date).isValid()) {
      console.error("isToday: Invalid or missing date");
      return false;
    }
    return dayjs(date).startOf("day").isSame(dayjs().startOf("day"));
  } catch (error) {
    console.error("isToday: Error", { error, date });
    return false;
  }
};

export const splitISODate = (isoString) => {
  try {
    if (!isoString || !dayjs(isoString).isValid()) {
      console.error("splitISODate: Invalid or missing date");
      return { date: null, time: null };
    }

    const date = dayjs(isoString);

    return {
      date: date,
      time: date.format(dateTimeFormats[2]),
    };
  } catch (error) {
    console.error("splitISODate: Error", { error, isoString });
    return { date: null, time: null };
  }
};

export const formatToISODate = (date, time) => {
  try {
    if (!date && !time) {
      return null;
    }

    if (!date && time) {
      date = dayjs();
    }

    if (!time) {
      return dayjs(date).startOf("day").toISOString();
    }

    const [hours, minutes] = time.replace(/[ap]m/i, "").split(":");
    const isPM = /pm/i.test(time);

    if (isNaN(hours) || isNaN(minutes)) {
      console.error("formatToISODate: Invalid time format", { time });
      return dayjs(date).startOf("day").toISOString();
    }

    return dayjs(date)
      .hour(isPM ? parseInt(hours) + 12 : parseInt(hours))
      .minute(parseInt(minutes))
      .second(0)
      .toISOString();
  } catch (error) {
    console.error("formatToISODate: Error", { error, date, time });
    return null;
  }
};

export const convertTimezone = (
  dateString,
  timeString,
  sourceTimezone,
  targetTimezone,
) => {
  if (!dateString || !timeString)
    return formatTimeTo12HrFormat(timeString || "");

  if (!sourceTimezone || !targetTimezone)
    return formatTimeTo12HrFormat(timeString);

  try {
    const sourceDate = new Date(
      `${dateString}T${timeString}:00`.replace(/Z$/, ""),
    );
    const options = {
      timeZone: targetTimezone,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };

    return sourceDate.toLocaleTimeString("en-US", options);
  } catch (error) {
    console.error("Timezone conversion error:", error);
    return formatTimeTo12HrFormat(timeString);
  }
};

export const formatTimeTo12HrFormat = (timeString) => {
  if (!timeString) return "";
  try {
    if (timeString.includes("AM") || timeString.includes("PM")) {
      return timeString;
    }
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const formattedHour = hour % 12 || 12;
    return `${formattedHour}:${(minutes || "00").padStart(2, "0")} ${ampm}`;
  } catch (error) {
    return timeString;
  }
};

export const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch (error) {
    return dateString;
  }
};

export const formatDateTimeWithTimezone = (
  dateString,
  userTimezone = "UTC",
) => {
  if (!dateString) return "N/A";
  try {
    const cleanDateString = dateString.replace(
      /\s+([+-]\d{4}\s+[A-Z]{3}|\([A-Z]{3,4}\)|[A-Z]{3,4})$/i,
      "",
    );
    const date = new Date(cleanDateString);

    if (isNaN(date.getTime())) return "N/A";
    return (
      date
        .toLocaleString("en-US", {
          timeZone: userTimezone,
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })
        .replace(" ", "") +
      ", " +
      date.toLocaleString("en-US", {
        timeZone: userTimezone,
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    );
  } catch (error) {
    console.error("Date parsing error:", error);
    return "N/A";
  }
};

export const formatDateToString = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const cleanDateString = dateString.replace(
      /\s+([+-]\d{4}\s+[A-Z]{3}|\([A-Z]{3,4}\)|[A-Z]{3,4})$/i,
      "",
    );
    const date = new Date(cleanDateString);
    if (isNaN(date.getTime())) return "N/A";

    const time = date
      .toLocaleString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .replace(" ", "");

    const dateFormatted = date.toLocaleString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

    return `${time}, ${dateFormatted}`;
  } catch (error) {
    console.error("Date parsing error:", error);
    return "N/A";
  }
};

export const getTimezoneOffset = (timezone) => {
  const offsets = {
    "America/Los_Angeles": -420,
    "America/Denver": -360,
    "America/Chicago": -300,
    "America/New_York": -240,
    "America/Anchorage": -480,
    "America/Adak": -540,
    "America/Phoenix": -420,
    "America/Vancouver": -420,
    "America/Edmonton": -360,
    "America/Winnipeg": -300,
    "America/Toronto": -240,
    "America/Halifax": -180,
    "America/St_Johns": -150,
    "America/Mexico_City": -300,
    "America/Bogota": -300,
    "America/Caracas": -240,
    "America/Manaus": -240,
    "America/Sao_Paulo": -180,
    "America/Argentina/Buenos_Aires": -180,
    "America/Santiago": -180,
    "Europe/London": 0,
    "Europe/Dublin": 0,
    "Europe/Lisbon": 0,
    "Europe/Paris": 60,
    "Europe/Brussels": 60,
    "Europe/Amsterdam": 60,
    "Europe/Berlin": 60,
    "Europe/Rome": 60,
    "Europe/Madrid": 60,
    "Europe/Vienna": 60,
    "Europe/Stockholm": 60,
    "Europe/Zurich": 60,
    "Europe/Prague": 60,
    "Europe/Warsaw": 60,
    "Europe/Budapest": 60,
    "Europe/Athens": 120,
    "Europe/Bucharest": 120,
    "Europe/Helsinki": 120,
    "Europe/Kiev": 120,
    "Europe/Riga": 120,
    "Europe/Sofia": 120,
    "Europe/Tallinn": 120,
    "Europe/Vilnius": 120,
    "Europe/Istanbul": 180,
    "Europe/Minsk": 180,
    "Europe/Moscow": 180,
    "Asia/Kolkata": 330,
    "Asia/Dubai": 240,
    "Asia/Karachi": 300,
    "Asia/Dhaka": 360,
    "Asia/Bangkok": 420,
    "Asia/Jakarta": 420,
    "Asia/Shanghai": 480,
    "Asia/Singapore": 480,
    "Asia/Hong_Kong": 480,
    "Asia/Taipei": 480,
    "Asia/Seoul": 540,
    "Asia/Tokyo": 540,
    "Australia/Perth": 480,
    "Australia/Darwin": 570,
    "Australia/Adelaide": 570,
    "Australia/Brisbane": 600,
    "Australia/Sydney": 600,
    "Australia/Melbourne": 600,
    "Australia/Hobart": 600,
    "Pacific/Auckland": 720,
    "Pacific/Fiji": 720,
    "Pacific/Honolulu": -600,
    "Atlantic/Azores": -60,
    "Atlantic/Cape_Verde": -60,
    "Atlantic/Reykjavik": 0,
    "Africa/Casablanca": 0,
    "Africa/Lagos": 60,
    "Africa/Cairo": 120,
    "Africa/Johannesburg": 120,
    "Africa/Nairobi": 180,
    UTC: 0,
  };

  return offsets[timezone] || 0;
};

export const formatDateToMonthDay = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    timeZone: "UTC",
  });
};

export const formatTimestamp = (
  dateString,
  formatType = "timestamp",
  customFormat = null,
  useTimezone = true,
) => {
  if (!dateString) return formatType === "timestamp" ? "N/A" : "";

  try {
    let date = dayjs(dateString).utc();
    if (!date.isValid()) return formatType === "timestamp" ? "N/A" : "";

    if (useTimezone) {
      const timezoneInitialized =
        store.getters["workspace/timezoneInitialized"];

      if (!timezoneInitialized) {
        store.dispatch("workspace/fetchUserTimezone");
      }

      const userTimezone = store.getters["workspace/userTimezone"];
      if (userTimezone) {
        const sourceOffsetMinutes = date.utcOffset();
        const targetOffsetMinutes = getTimezoneOffset(userTimezone);
        const offsetDiff = targetOffsetMinutes - sourceOffsetMinutes;
        date = date.add(offsetDiff, "minute");
      }
    }

    let format;
    if (customFormat) {
      format = customFormat;
    } else if (typeof formatType === "string" && formatTypes[formatType]) {
      format = formatTypes[formatType];
    } else if (typeof formatType === "string") {
      format = formatType;
    } else {
      format = formatTypes.timestamp;
    }

    return date.format(format);
  } catch (error) {
    if (formatType === "timestamp") {
      console.error("Error formatting timestamp:", error);
    }
    return formatType === "timestamp" ? "N/A" : "";
  }
};

export const formatChatListTime = (dateString) => {
  if (!dateString) return "";

  try {
    let date = dayjs(dateString).utc();
    if (!date.isValid()) return "";

    const timezoneInitialized = store.getters["workspace/timezoneInitialized"];
    if (!timezoneInitialized) {
      store.dispatch("workspace/fetchUserTimezone");
    }
    const userTimezone = store.getters["workspace/userTimezone"];
    if (userTimezone) {
      const sourceOffsetMinutes = date.utcOffset();
      const targetOffsetMinutes = getTimezoneOffset(userTimezone);
      const offsetDiff = targetOffsetMinutes - sourceOffsetMinutes;
      date = date.add(offsetDiff, "minute");
    }

    const now = userTimezone
      ? dayjs().utc().add(getTimezoneOffset(userTimezone), "minute")
      : dayjs();

    const startOfToday = now.startOf("day");
    const startOfYesterday = startOfToday.subtract(1, "day");

    if (date.isSame(startOfToday, "day") || date.isAfter(startOfToday)) {
      const diffMs = now.diff(date);
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return "now";
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffHr = Math.floor(diffMin / 60);
      return `${diffHr}hr ago`;
    }

    if (
      date.isSame(startOfYesterday, "day") ||
      (date.isAfter(startOfYesterday) && date.isBefore(startOfToday))
    ) {
      return "Yesterday";
    }

    return date.format("DD/MM/YYYY");
  } catch (error) {
    console.error("Error formatting chat list time:", error);
    return "";
  }
};

export const formatVisualizationDateTimeValue = (value) => {
  if (!value) return "--";
  if (typeof value === "string") {
    const normalized = normalizeToISO(value);
    if (normalized) {
      const date = new Date(normalized);
      if (!isNaN(date.getTime())) {
        const isMidnightUTC =
          date.getUTCHours() === 0 &&
          date.getUTCMinutes() === 0 &&
          date.getUTCSeconds() === 0;
        const formatType = isMidnightUTC ? "dateOnly" : "D MMM YYYY, h:mm A";
        return formatTimestamp(normalized, formatType, null, false) || value;
      }
    }
  }
  return value;
};

export const getISOWeekNumber = (utcDate) => {
  const d = new Date(
    Date.UTC(
      utcDate.getUTCFullYear(),
      utcDate.getUTCMonth(),
      utcDate.getUTCDate(),
    ),
  );
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return {
    week: Math.ceil(((d - yearStart) / 86400000 + 1) / 7),
    year: d.getUTCFullYear(),
  };
};

export const detectDatesPeriodType = (dates) => {
  if (!dates.length) return "unknown";
  if (
    dates.some(
      (d) =>
        d.getUTCHours() !== 0 ||
        d.getUTCMinutes() !== 0 ||
        d.getUTCSeconds() !== 0,
    )
  )
    return "hourly";
  if (!dates.every((d) => d.getUTCDate() === 1)) {
    const dayOfWeek = dates[0].getUTCDay();
    if (dates.length > 1 && dates.every((d) => d.getUTCDay() === dayOfWeek))
      return "weekly";
    return "daily";
  }
  const quarterMonths = [0, 3, 6, 9];
  if (dates.every((d) => d.getUTCMonth() === 0)) return "yearly";
  if (dates.every((d) => quarterMonths.includes(d.getUTCMonth())))
    return "quarterly";
  return "monthly";
};

export const formatDateByPeriod = (value, periodType) => {
  if (!value || typeof value !== "string") return value ?? "--";
  const normalized = normalizeToISO(value);
  if (!normalized) return formatVisualizationDateTimeValue(value);
  const date = new Date(normalized);
  if (isNaN(date.getTime())) return formatVisualizationDateTimeValue(value);
  if (periodType === "hourly")
    return (
      formatTimestamp(normalized, "D MMM YYYY, h:mm A", null, false) || value
    );
  if (periodType === "weekly") {
    const { week, year } = getISOWeekNumber(date);
    return `Week ${week}, ${year}`;
  }
  if (periodType === "monthly")
    return formatTimestamp(normalized, "MMM YYYY", null, false) || value;
  if (periodType === "yearly")
    return formatTimestamp(normalized, "YYYY", null, false) || value;
  if (periodType === "quarterly") {
    const quarter = Math.floor(date.getUTCMonth() / 3) + 1;
    return `Q${quarter} ${date.getUTCFullYear()}`;
  }
  return formatTimestamp(normalized, "D MMM YYYY", null, false) || value;
};

export const normalizeToISO = (value) => {
  if (value.includes("T")) return value;
  if (value.includes("+0000")) {
    return value.replace(" ", "T").replace(/\s*\+0000\s*utc$/i, "Z");
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return `${value}T00:00:00Z`;
  }
  return null;
};

export const dateFilterToISO = (dateFilter) => {
  if (!dateFilter || typeof dateFilter !== "object") return null;
  const { option, start_date } = dateFilter;
  if (!option) return null;
  if (option === "custom") {
    return start_date ? normalizeToISO(start_date) : null;
  }
  const range = calculateDateRange(option);
  return range?.start ? normalizeToISO(range.start) : null;
};

export const formatSecondsToTime = (seconds) => {
  if (!seconds && seconds !== 0) return "00:00";

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
};

export const msToMinutes = (milliseconds) => {
  return milliseconds ? milliseconds / 60000 : 0;
};

export const msToSeconds = (milliseconds) => {
  return milliseconds ? milliseconds / 1000 : 0;
};

export const minutesToMs = (minutes) => {
  return minutes ? minutes * 60000 : 0;
};

export const secondsToMs = (seconds) => {
  return seconds ? seconds * 1000 : 0;
};

export const convertToHHMM = (timeString) => {
  if (!timeString) return "";
  return dayjs(timeString, "h:mma").format("HH:mm");
};

export const convertToDisplayFormat = (timeString) => {
  if (!timeString) return "";
  return dayjs(timeString, "HH:mm").format("h:mma");
};

export const generateBusinessHourTimeOptions = (
  startTime,
  endTime,
  bufferTime = 1,
  generateTimeOptions = null,
) => {
  if (!generateTimeOptions) {
    generateTimeOptions = () => {
      const times = [];
      const periods = ["am", "pm"];

      periods.forEach((period) => {
        for (let hour = 0; hour < 12; hour++) {
          for (let minutes = 0; minutes < 60; minutes += 15) {
            let displayHour = hour === 0 ? 12 : hour;
            let displayMinutes = minutes === 0 ? "00" : minutes;
            times.push(`${displayHour}:${displayMinutes}${period}`);
          }
        }
      });

      return times;
    };
  }

  if (!startTime || !endTime) {
    return generateTimeOptions().filter((time) => {
      const hour = dayjs(time, "h:mma").hour();
      return hour >= 9 && hour <= 16;
    });
  }

  const start = dayjs(startTime, "h:mma");
  let end = dayjs(endTime, "h:mma");

  if (end.hour() < start.hour()) {
    end = end.add(1, "day");
  }

  const endWithBuffer = end.subtract(bufferTime, "hour");

  const options = [];
  let current = start.clone();
  while (current.isBefore(endWithBuffer) || current.isSame(endWithBuffer)) {
    options.push(current.format("h:mma"));
    current = current.add(15, "minutes");
  }
  return options;
};

export const formatCompactRelativeTime = (dateString) => {
  if (!dateString) return "--";

  try {
    const date = dayjs(dateString);
    if (!date.isValid()) return "--";

    const now = dayjs();

    const diffInMinutes = now.diff(date, "minute");
    const diffInHours = now.diff(date, "hour");
    const diffInDays = now.diff(date, "day");
    const diffInWeeks = now.diff(date, "week");
    const diffInMonths = now.diff(date, "month");
    const diffInYears = now.diff(date, "year");

    // Less than 1 hour
    if (diffInHours < 1) {
      if (diffInMinutes < 1) return "now";
      return `${diffInMinutes}m ago`;
    }

    // Hours (for today)
    if (diffInDays === 0) {
      return `${diffInHours}h ago`;
    }

    // Days
    if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    }

    // Weeks
    if (diffInWeeks < 4) {
      return `${diffInWeeks}w ago`;
    }

    // Months
    if (diffInMonths < 12) {
      return `${diffInMonths}mo ago`;
    }

    // Years
    return `${diffInYears}y ago`;
  } catch (error) {
    console.error("formatCompactRelativeTime: Error", { error, dateString });
    return "--";
  }
};

export const getTimeOptions = () => {
  const now = dayjs();
  const { due_time } = getInitialDateTime();

  const today = `${now.format("ddd DD MMM")}, ${due_time}`;
  const tomorrow = `${now.add(1, "day").format("ddd DD MMM")}, ${due_time}`;
  const in2Days = `${now.add(2, "days").format("ddd DD MMM")}, ${due_time}`;
  const in3Days = `${now.add(3, "days").format("ddd DD MMM")}, ${due_time}`;

  return [
    { value: "today", label: "Today", dateTime: today },
    { value: "tomorrow", label: "Tomorrow", dateTime: tomorrow },
    { value: "in_2_days", label: "In 2 days", dateTime: in2Days },
    { value: "in_3_days", label: "In 3 days", dateTime: in3Days },
    { value: "custom", label: "Custom time", dateTime: "" },
  ];
};

export const getSelectedDateTime = (
  selectedTimeOption,
  customDate,
  customTime,
) => {
  if (selectedTimeOption === "custom") {
    if (customDate && customTime) {
      const date = customDate.toDate();
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const formattedDate = `${day}/${month}/${year}`;
      return { date: formattedDate, time: customTime };
    }
    return null;
  }

  const days =
    { today: 0, tomorrow: 1, in_2_days: 2, in_3_days: 3 }[selectedTimeOption] ||
    0;
  const targetDate = dayjs().add(days, "day");

  const date = targetDate.toDate();
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const formattedDate = `${day}/${month}/${year}`;

  return { date: formattedDate, time: getInitialDateTime().due_time };
};

export const formatTimeDisplay = (value, unit) => {
  if (value === null || value === undefined) return "";

  try {
    if (unit === "mins") {
      const totalSeconds = Math.floor(value * 60);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);

      if (hours > 0 && minutes > 0) {
        return `${hours}h ${minutes}m`;
      }
      if (hours > 0) {
        return `${hours}h`;
      }
      return `${Math.floor(value)}m`;
    }

    if (unit === "secs") {
      const totalSeconds = Math.floor(value);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;

      if (minutes > 0 && seconds > 0) {
        return `${minutes}m ${seconds}s`;
      }
      if (minutes > 0) {
        return `${minutes}m`;
      }

      if (value < 1) {
        return `${value}s`;
      }
      return `${seconds}s`;
    }

    return value.toString();
  } catch (error) {
    console.error("formatTimeDisplay: Error", { error, value, unit });
    return value.toString();
  }
};

export const formatDateForCampaign = (date) => {
  if (!date) return "";
  const d = dayjs(date);
  if (!d.isValid()) return "";

  const day = d.date();
  const month = d.format("MMM");
  const year = d.year();
  const time = d.format("h:mma");

  return `${day} ${month} ${year}, ${time}`;
};

export const formatTimeSinceChange = (timestampMs) => {
  if (!timestampMs) return "-";

  const now = Date.now();
  const diffInSeconds = Math.floor((now - timestampMs) / 1000);

  if (diffInSeconds < 60) return `${diffInSeconds}s`;

  const minutes = Math.floor(diffInSeconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  const remainingHours = hours % 24;
  const remainingMinutes = minutes % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (remainingHours > 0) parts.push(`${remainingHours}h`);
  if (remainingMinutes > 0) parts.push(`${remainingMinutes}m`);

  return parts.length > 0 ? parts.join(" ") : "0m";
};

export const calculateTimeRemaining = (
  dueDate,
  dueTime = null,
  timezone = null,
) => {
  if (!dueDate) return null;

  try {
    let targetDate = dayjs(dueDate);
    if (!targetDate.isValid()) return null;

    if (dueTime) {
      const timeMatch = dueTime.match(/(\d+):(\d+)\s*(am|pm)/i);
      if (timeMatch) {
        let hours = parseInt(timeMatch[1]);
        const minutes = parseInt(timeMatch[2]);
        const isPM = timeMatch[3].toLowerCase() === "pm";

        if (isPM && hours !== 12) hours += 12;
        if (!isPM && hours === 12) hours = 0;

        targetDate = targetDate.hour(hours).minute(minutes).second(0);
      }
    } else {
      targetDate = targetDate.endOf("day");
    }

    // Get current time in the user's timezone
    let now = dayjs();
    if (timezone) {
      const nowInTimezone = new Date().toLocaleString("en-US", {
        timeZone: timezone,
      });
      now = dayjs(nowInTimezone);
    }

    const diffMs = targetDate.diff(now);

    const isOverdue = diffMs < 0;
    const absDiffMs = Math.abs(diffMs);

    const days = Math.floor(absDiffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (absDiffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    );
    const minutes = Math.floor((absDiffMs % (1000 * 60 * 60)) / (1000 * 60));

    return {
      isOverdue,
      days,
      hours,
      minutes,
    };
  } catch (error) {
    console.error("calculateTimeRemaining: Error", {
      error,
      dueDate,
      dueTime,
      timezone,
    });
    return null;
  }
};

export const formatTimeRemaining = (timeInfo) => {
  if (!timeInfo) return null;

  const { isOverdue, days, hours, minutes } = timeInfo;
  const parts = [];

  if (days > 0) {
    parts.push(`${days} ${days === 1 ? "day" : "days"}`);
  }
  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
  }
  if (minutes > 0 || parts.length === 0) {
    parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
  }

  let result = "";
  if (parts.length === 1) {
    result = parts[0];
  } else if (parts.length === 2) {
    result = parts.join(" and ");
  } else if (parts.length === 3) {
    result = `${parts[0]}, ${parts[1]}, and ${parts[2]}`;
  }

  return isOverdue ? `Overdue by ${result}` : result;
};

export const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false;
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

const numberRange = (start, end) => {
  const result = [];
  for (let i = start; i < end; i++) result.push(i);
  return result;
};

export const createDisabledEndDate = (
  getStartDate,
  { allowSameDay = () => false } = {},
) => {
  return (current) => {
    const startDate = getStartDate();
    if (!startDate || !current) return false;
    if (allowSameDay()) {
      return current.isBefore(startDate, "day");
    }
    return !current.isAfter(startDate, "day");
  };
};


export const createDisabledEndTime = (getStartDate) => {
  return (current) => {
    const startDate = getStartDate();
    if (!startDate || !current) return {};
    if (!current.isSame(startDate, "day")) return {};
    const startHour = startDate.hour();
    const startMinute = startDate.minute();
    const startSecond = startDate.second();
    return {
      disabledHours: () => numberRange(0, startHour),
      disabledMinutes: (h) =>
        h === startHour ? numberRange(0, startMinute) : [],
      disabledSeconds: (h, m) =>
        h === startHour && m === startMinute
          ? numberRange(0, startSecond + 1)
          : [],
    };
  };
};
